import { useEffect, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import {
  getSupplierFinance,
  type SupplierFinance,
  type PaymentStatusUpper,
} from '@/lib/operatorApi';
import { formatKop, formatDate, formatDateTime } from '@/lib/format';

export default function SupplierFinancePage() {
  const [data, setData] = useState<SupplierFinance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getSupplierFinance()
      .then((d) => { if (alive) setData(d); })
      .catch((e: Error) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const c = data?.counts;

  return (
    <div>
      <PageHeader
        eyebrow="Поставщики"
        title="Финансы поставщиков"
        description={
          data
            ? `Подписка «Магазин поставщика» — ${formatKop(data.priceKop)}/мес. MRR считается как активные × цена.`
            : 'Подписки, выручка и просрочки по магазину «Закуп».'
        }
      />

      <div className="page-pad">
        {error && (
          <div className="mb-6 border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {error}
          </div>
        )}

        {/* Деньги */}
        <div className="grid grid-cols-2 gap-px bg-line border border-line lg:grid-cols-4">
          <Kpi label="MRR" value={data ? formatKop(data.mrr) : '—'} hint="активные × цена" />
          <Kpi label="Выручка 30 дней" value={data ? formatKop(data.revenueLast30d) : '—'} />
          <Kpi label="Выручка всего" value={data ? formatKop(data.revenueTotal) : '—'} />
          <Kpi
            label="Списаний / ошибок"
            value={data ? `${data.succeededCount} / ${data.failedCount}` : '—'}
            accent={data && data.failedCount > 0 ? 'red' : undefined}
          />
        </div>

        {/* Подписки по статусам */}
        <div className="mt-6 grid grid-cols-2 gap-px bg-line border border-line lg:grid-cols-5">
          <Kpi label="Всего" value={c ? String(c.total) : '—'} />
          <Kpi label="Триал" value={c ? String(c.trial) : '—'} />
          <Kpi label="Активные" value={c ? String(c.active) : '—'} accent="green" />
          <Kpi label="Просрочены" value={c ? String(c.pastDue) : '—'} accent={c && c.pastDue > 0 ? 'red' : undefined} />
          <Kpi label="Отменены" value={c ? String(c.cancelled) : '—'} />
        </div>

        {/* Ближайшие списания */}
        <div className="card mt-6">
          <div className="flex items-center gap-2 border-b border-line px-4 py-4 md:px-5">
            <CalendarClock size={15} className="text-ink/50" />
            <h2 className="text-[14px] font-medium">Ближайшие автосписания (7 дней)</h2>
            {data && (
              <span className="ml-auto text-[12px] text-ink/50">{data.upcomingRenewals.length}</span>
            )}
          </div>
          <ul className="divide-y divide-line">
            {data?.upcomingRenewals.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-3 md:px-5">
                <span className="min-w-0 truncate text-[13px] font-medium text-ink">{r.name}</span>
                <span className="shrink-0 font-mono text-[12px] text-ink/60">
                  {r.currentPeriodEnd ? formatDate(r.currentPeriodEnd) : '—'}
                </span>
              </li>
            ))}
            {data && data.upcomingRenewals.length === 0 && (
              <li className="px-4 py-8 text-center text-[13px] text-ink/50">
                Нет списаний в ближайшую неделю
              </li>
            )}
            {loading && !data && (
              <li className="px-4 py-8 text-center text-[13px] text-ink/40">Загрузка…</li>
            )}
          </ul>
        </div>

        {/* История платежей */}
        <div className="card mt-6">
          <div className="border-b border-line px-4 py-4 md:px-5">
            <h2 className="text-[14px] font-medium">Платежи поставщиков</h2>
          </div>

          {/* Desktop */}
          <div className="hidden md:block">
            <table className="table-grid">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Поставщик</th>
                  <th>Метод</th>
                  <th>Статус</th>
                  <th className="text-right">Сумма</th>
                  <th className="text-right">Дата</th>
                </tr>
              </thead>
              <tbody>
                {data?.recentPayments.map((p) => (
                  <tr key={p.id}>
                    <td className="font-mono text-[12px]">{p.id.slice(0, 8)}</td>
                    <td className="font-medium">{p.supplierName}</td>
                    <td className="text-ink/70">{p.method ?? '—'}</td>
                    <td><span className={badgeClass(p.status)}>{statusLabel(p.status)}</span></td>
                    <td className="text-right font-mono">{formatKop(p.amount)}</td>
                    <td className="text-right font-mono text-[12px] text-ink/60">
                      {formatDateTime(p.createdAt)}
                    </td>
                  </tr>
                ))}
                {data && data.recentPayments.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-ink/50">Платежей пока нет</td>
                  </tr>
                )}
                {loading && !data && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-ink/40">Загрузка…</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <ul className="divide-y divide-line md:hidden">
            {data?.recentPayments.map((p) => (
              <li key={p.id} className="px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-medium text-ink">{p.supplierName}</div>
                    <div className="mt-0.5 font-mono text-[11px] text-ink/50">
                      {p.id.slice(0, 8)} · {p.method ?? '—'}
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] text-ink/50">
                      {formatDateTime(p.createdAt)}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-mono text-[14px] font-medium">{formatKop(p.amount)}</div>
                    <span className={`${badgeClass(p.status)} mt-1`}>{statusLabel(p.status)}</span>
                  </div>
                </div>
              </li>
            ))}
            {data && data.recentPayments.length === 0 && (
              <li className="px-4 py-8 text-center text-[13px] text-ink/50">Платежей пока нет</li>
            )}
            {loading && !data && (
              <li className="px-4 py-8 text-center text-[13px] text-ink/40">Загрузка…</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

function badgeClass(status: PaymentStatusUpper): string {
  switch (status) {
    case 'SUCCEEDED': return 'badge-green';
    case 'FAILED':    return 'badge-red';
    case 'REFUNDED':  return 'badge-ink';
    default:          return 'badge-amber';
  }
}

function statusLabel(status: PaymentStatusUpper): string {
  switch (status) {
    case 'SUCCEEDED': return 'успешно';
    case 'FAILED':    return 'ошибка';
    case 'REFUNDED':  return 'возврат';
    case 'PENDING':   return 'в ожидании';
  }
}

function Kpi({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: 'red' | 'green';
}) {
  const color = accent === 'red' ? 'text-red-600' : accent === 'green' ? 'text-green-700' : '';
  return (
    <div className="bg-white p-6">
      <div className="kpi-label">{label}</div>
      <div className={'mt-4 kpi-value ' + color}>{value}</div>
      {hint && <div className="mt-1 text-[11px] text-ink/40">{hint}</div>}
    </div>
  );
}
